import React, { useState, useEffect, useRef } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Users, Search, Loader2, User, Mail, Phone, MapPin, Ticket, 
  CalendarDays, Edit, Plus, Minus, History, CreditCard, ArrowUpCircle, ArrowDownCircle, XCircle,
  Download, Upload, Key, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatApiError } from '../lib/apiError';
import { formatActivity } from '../lib/formatActivity';

export const StudioClientsPage = () => {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activityTypes, setActivityTypes] = useState([]);

  // Client detail dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit client dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '', last_name: '', phone: '', 
    address: '', address_line_2: '', city: '', county: '', postal_code: '', country: 'Ireland'
  });
  const [editLoading, setEditLoading] = useState(false);

  // Booking/ClassPass filters in client detail
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingStatusFilter, setBookingStatusFilter] = useState('all');
  const [classPassSearch, setClassPassSearch] = useState('');
  const [classPassStatusFilter, setClassPassStatusFilter] = useState('all');

  // Adjust ClassPass dialog
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [adjustReason, setAdjustReason] = useState('');

  // Import/Export
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importSendWelcome, setImportSendWelcome] = useState(false);
  const [importAssignPass, setImportAssignPass] = useState('');
  const [importCredits, setImportCredits] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [classPasses, setClassPasses] = useState([]);
  const fileInputRef = useRef(null);

  // Password reset
  const [resettingPassword, setResettingPassword] = useState(null);
  const [adjustLoading, setAdjustLoading] = useState(false);

  // Adjust Credits dialog
  const [showAdjustCreditsDialog, setShowAdjustCreditsDialog] = useState(false);
  const [adjustCreditsAmount, setAdjustCreditsAmount] = useState(1);
  const [adjustCreditsReason, setAdjustCreditsReason] = useState('');
  const [adjustCreditsActivityType, setAdjustCreditsActivityType] = useState('general');
  const [adjustCreditsLoading, setAdjustCreditsLoading] = useState(false);

  // Credit History dialog
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Cancel Booking dialog
  const [showCancelBookingDialog, setShowCancelBookingDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelWithRefund, setCancelWithRefund] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchActivityTypes();
    fetchClassPasses();
  }, []);

  const fetchClients = async () => {
    try {
      const data = await apiGet('/api/studio/clients');
      setClients(data);
    } catch (e) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassPasses = async () => {
    try {
      const data = await apiGet('/api/class-passes');
      setClassPasses(data.filter(p => p.is_active));
    } catch (e) {
      console.error('Failed to fetch class passes:', e);
    }
  };

  const fetchActivityTypes = async () => {
    try {
      const data = await apiGet('/api/studios/me');
      setActivityTypes(data.activity_types || ['general']);
    } catch (e) {
      console.error('Failed to fetch activity types');
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/studio/clients', { query: { search: searchTerm } });
      setClients(data);
    } catch (e) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const openClientDetail = async (client) => {
    setSelectedClient(client);
    setShowDetailDialog(true);
    setDetailLoading(true);
    try {
      // Iter-46i: fetch the single-client detail endpoint (returns
      // `{ client, bookings, class_passes, subscriptions, ... }`). The
      // previous code hit the list endpoint which returned an array and made
      // every downstream `clientDetails.client.first_name` access throw.
      const data = await apiGet('/api/studio/clients/{client_id}', {
        params: { client_id: client.id },
      });
      setClientDetails(data);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load client details'));
    } finally {
      setDetailLoading(false);
    }
  };

  const openEditDialog = () => {
    setEditForm({
      first_name: clientDetails.client.first_name || '',
      last_name: clientDetails.client.last_name || '',
      // Iter-46j: studio admins can correct a client's email address (e.g.
      // typos at signup, role-account email change). Backend already accepts
      // this on PUT /studio/clients/{client_id}.
      email: clientDetails.client.email || '',
      phone: clientDetails.client.phone || '',
      address: clientDetails.client.address || '',
      address_line_2: clientDetails.client.address_line_2 || '',
      city: clientDetails.client.city || '',
      county: clientDetails.client.county || '',
      postal_code: clientDetails.client.postal_code || '',
      country: clientDetails.client.country || 'Ireland'
    });
    setShowEditDialog(true);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await apiPut('/api/studio/clients/{client_id}', { params: { client_id: selectedClient.id }, body: editForm });
      toast.success('Client updated!');
      setShowEditDialog(false);
      // Refresh details (Iter-46i: detail endpoint, not list)
      const data = await apiGet('/api/studio/clients/{client_id}', {
        params: { client_id: selectedClient.id },
      });
      setClientDetails(data);
      fetchClients();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update client'));
    } finally {
      setEditLoading(false);
    }
  };

  const openAdjustDialog = (pass) => {
    setSelectedPass(pass);
    setAdjustAmount(0);
    setAdjustReason('');
    setShowAdjustDialog(true);
  };

  const handleAdjustClassPass = async (e) => {
    e.preventDefault();
    if (adjustAmount === 0) {
      toast.error('Please enter an adjustment amount');
      return;
    }
    setAdjustLoading(true);
    try {
      await apiPut('/api/studio/clients/{client_id}/classpasses/{pass_id}/adjust', { params: { client_id: selectedClient.id, pass_id: selectedPass.id }, body: {
        adjustment: adjustAmount,
        reason: adjustReason
      } });
      toast.success('ClassPass adjusted!');
      setShowAdjustDialog(false);
      // Refresh details (Iter-46i: detail endpoint)
      const data = await apiGet('/api/studio/clients/{client_id}', {
        params: { client_id: selectedClient.id },
      });
      setClientDetails(data);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to adjust ClassPass'));
    } finally {
      setAdjustLoading(false);
    }
  };

  // Adjust Credits handlers
  const openAdjustCreditsDialog = () => {
    setAdjustCreditsAmount(1);
    setAdjustCreditsReason('');
    setAdjustCreditsActivityType(activityTypes[0] || 'general');
    setShowAdjustCreditsDialog(true);
  };

  const handleAdjustCredits = async (e) => {
    e.preventDefault();
    if (adjustCreditsAmount === 0) {
      toast.error('Please enter a non-zero amount');
      return;
    }
    setAdjustCreditsLoading(true);
    try {
      const data = await apiPost('/api/studio/clients/{client_id}/add-credits', { params: { client_id: selectedClient.id }, body: {
        amount: adjustCreditsAmount,
        reason: adjustCreditsReason,
        activity_type: adjustCreditsActivityType
      } });
      toast.success(data.message || 'Credits adjusted successfully!');
      setShowAdjustCreditsDialog(false);
      // Refresh details (Iter-46i: detail endpoint)
      const detailsRes = await apiGet('/api/studio/clients/{client_id}', {
        params: { client_id: selectedClient.id },
      });
      setClientDetails(detailsRes);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to adjust credits'));
    } finally {
      setAdjustCreditsLoading(false);
    }
  };

  // Credit History handlers
  const openCreditHistory = async () => {
    setShowHistoryDialog(true);
    setHistoryLoading(true);
    try {
      const data = await apiGet('/api/studio/clients/{client_id}/credit-history', { params: { client_id: selectedClient.id } });
      setCreditHistory(data);
    } catch (e) {
      toast.error('Failed to load credit history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Cancel Booking handlers
  const openCancelBookingDialog = (booking) => {
    setSelectedBooking(booking);
    setCancelWithRefund(true);
    setShowCancelBookingDialog(true);
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking || !selectedClient) return;

    setCancelLoading(true);
    // Iter-46c: cancellation can take several seconds (Ryft refund).
    const loadingToastId = toast.loading('Processing cancellation…');
    try {
      await apiDelete('/api/studio/clients/{client_id}/bookings/{booking_id}', { params: { client_id: selectedClient.id, booking_id: selectedBooking.id }, query: { refund: cancelWithRefund } });
      toast.dismiss(loadingToastId);
      toast.success(`Booking cancelled${cancelWithRefund ? ' with refund' : ''}`);
      setShowCancelBookingDialog(false);
      setSelectedBooking(null);
      // Refresh client details (Iter-46i: detail endpoint)
      const data = await apiGet('/api/studio/clients/{client_id}', {
        params: { client_id: selectedClient.id },
      });
      setClientDetails(data);
    } catch (e) {
      toast.dismiss(loadingToastId);
      toast.error(formatApiError(e, 'Failed to cancel booking'));
    } finally {
      setCancelLoading(false);
    }
  };

  // Export clients
  const handleExportClients = async () => {
    try {
      const response = await apiGet('/api/studio/clients/export', { body: { responseType: 'blob' } });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Clients exported successfully!');
    } catch (e) {
      toast.error('Failed to export clients');
    }
  };

  // Download import template
  const handleDownloadTemplate = async () => {
    try {
      const response = await apiGet('/api/studio/clients/import/template', { body: { responseType: 'blob' } });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'client_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  // Import clients
  const handleImportClients = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    setImportResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('send_welcome_emails', importSendWelcome);
      if (importAssignPass && importAssignPass !== 'none') {
        formData.append('assign_class_pass_id', importAssignPass);
        formData.append('credits_per_client', importCredits);
      }
      
      const data = await apiPost('/api/studio/clients/import', { body: formData });
      
      setImportResults(data);
      if (data.imported > 0) {
        toast.success(`Imported ${data.imported} clients!`);
        fetchClients();
      }
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to import clients'));
    } finally {
      setImporting(false);
    }
  };

  // Trigger password reset for client
  const handlePasswordReset = async (clientId, clientEmail) => {
    if (!window.confirm(`Send password reset email to ${clientEmail}?`)) return;
    
    setResettingPassword(clientId);
    try {
      const data = await apiPost('/api/studio/clients/{client_id}/reset-password', { params: { client_id: clientId } });
      toast.success(data.message || 'Password reset email sent!');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to send reset email'));
    } finally {
      setResettingPassword(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="studio-clients-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Clients</h1>
            <p className="text-muted-foreground">Manage your studio's clients and their ClassPasses</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportClients} data-testid="export-clients-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => setShowImportDialog(true)} data-testid="import-clients-btn">
              <Upload className="w-4 h-4 mr-2" />
              Import Clients
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
              <Button onClick={handleSearch}>Search</Button>
            </div>
          </CardContent>
        </Card>

        {/* Clients List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Clients ({clients.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {clients.length > 0 ? (
              <div className="space-y-3">
                {clients.map((client, i) => (
                  <div
                    key={client.id}
                    // Bug R34 fix (Iter-68): stack on mobile (avatar+name row
                    // then stats row) so the stats no longer collide with the
                    // name and the row stays readable on narrow screens.
                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => openClientDetail(client)}
                    data-testid={`client-row-${i}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{client.first_name} {client.last_name}</p>
                        <p className="text-sm text-muted-foreground truncate">{client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between md:justify-end gap-4 md:gap-6 text-sm text-muted-foreground border-t md:border-0 pt-3 md:pt-0">
                      <div className="text-center">
                        <p className="font-semibold text-foreground">{client.total_bookings}</p>
                        <p>Bookings</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-foreground">{client.total_classpasses}</p>
                        <p>Passes</p>
                      </div>
                      <p className="text-xs hidden sm:block">Since {format(new Date(client.joined_at), 'MMM yyyy')}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No clients found</p>
                <p className="text-sm text-muted-foreground mt-1">Clients will appear here after they book a class</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Client Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {selectedClient?.first_name} {selectedClient?.last_name}
            </DialogTitle>
            <DialogDescription>{selectedClient?.email}</DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : clientDetails && (
            <Tabs defaultValue="profile" className="mt-4">
              <TabsList>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="bookings">Bookings ({clientDetails.bookings?.length || 0})</TabsTrigger>
                <TabsTrigger value="classpasses">ClassPasses ({clientDetails.class_passes?.length || 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg border">
                    <p className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />Email</p>
                    <p className="font-medium">{clientDetails.client.email}</p>
                  </div>
                  <div className="p-3 rounded-lg border">
                    <p className="text-sm text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />Phone</p>
                    <p className="font-medium">{clientDetails.client.phone || 'Not provided'}</p>
                  </div>
                  <div className="col-span-2 p-3 rounded-lg border">
                    <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />Address</p>
                    <p className="font-medium">
                      {clientDetails.client.address || clientDetails.client.city 
                        ? `${clientDetails.client.address || ''} ${clientDetails.client.city || ''} ${clientDetails.client.postal_code || ''}`.trim()
                        : 'Not provided'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={openEditDialog}>
                    <Edit className="w-4 h-4 mr-2" />Edit Profile
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handlePasswordReset(selectedClient.id, selectedClient.email)}
                    disabled={resettingPassword === selectedClient?.id}
                    data-testid="reset-password-btn"
                  >
                    {resettingPassword === selectedClient?.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    Reset Password
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="bookings" className="mt-4">
                {/* Search and Filter */}
                <div className="flex gap-2 mb-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search bookings..."
                      value={bookingSearch}
                      onChange={(e) => setBookingSearch(e.target.value)}
                      className="pl-10 h-9"
                    />
                  </div>
                  <Select value={bookingStatusFilter} onValueChange={setBookingStatusFilter}>
                    <SelectTrigger className="w-36 h-9">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {clientDetails.bookings?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {clientDetails.bookings
                      .filter(booking => {
                        const matchesSearch = !bookingSearch || 
                          booking.class_name?.toLowerCase().includes(bookingSearch.toLowerCase());
                        const matchesStatus = bookingStatusFilter === 'all' || booking.status === bookingStatusFilter;
                        return matchesSearch && matchesStatus;
                      })
                      .map((booking, i) => (
                      <div key={booking.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{booking.class_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {booking.class_start_time ? format(new Date(booking.class_start_time), 'MMM d, yyyy h:mm a') : 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={booking.status === 'confirmed' ? 'default' : 'secondary'}>{booking.status}</Badge>
                          {booking.status === 'confirmed' && (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => openCancelBookingDialog(booking)}
                              data-testid={`cancel-booking-${booking.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {clientDetails.bookings.filter(booking => {
                      const matchesSearch = !bookingSearch || 
                        booking.class_name?.toLowerCase().includes(bookingSearch.toLowerCase());
                      const matchesStatus = bookingStatusFilter === 'all' || booking.status === bookingStatusFilter;
                      return matchesSearch && matchesStatus;
                    }).length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No bookings match your filters</p>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No bookings yet</p>
                )}
              </TabsContent>

              <TabsContent value="classpasses" className="mt-4">
                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button size="sm" onClick={openAdjustCreditsDialog} data-testid="adjust-credits-btn">
                    <Edit className="w-4 h-4 mr-1" />Adjust Credits
                  </Button>
                  <Button size="sm" variant="outline" onClick={openCreditHistory} data-testid="view-history-btn">
                    <History className="w-4 h-4 mr-1" />View Credit History
                  </Button>
                </div>

                {/* Search and Filter */}
                <div className="flex gap-2 mb-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search class passes..."
                      value={classPassSearch}
                      onChange={(e) => setClassPassSearch(e.target.value)}
                      className="pl-10 h-9"
                    />
                  </div>
                  <Select value={classPassStatusFilter} onValueChange={setClassPassStatusFilter}>
                    <SelectTrigger className="w-36 h-9">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="used">Used</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {clientDetails.class_passes?.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {clientDetails.class_passes
                      .filter(pass => {
                        const matchesSearch = !classPassSearch || 
                          pass.class_pass_name?.toLowerCase().includes(classPassSearch.toLowerCase()) ||
                          pass.activity_type?.toLowerCase().includes(classPassSearch.toLowerCase());
                        const matchesStatus = classPassStatusFilter === 'all' || pass.status === classPassStatusFilter;
                        return matchesSearch && matchesStatus;
                      })
                      .map((pass, i) => (
                      <div key={pass.id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <Badge variant="outline" className="mb-1">{formatActivity(pass.activity_type)}</Badge>
                            <p className="font-medium">{pass.class_pass_name}</p>
                          </div>
                          <Badge variant={pass.status === 'active' ? 'default' : 'secondary'}>{pass.status}</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1">
                              <Ticket className="w-4 h-4" />
                              {pass.passes_remaining}/{pass.passes_total} remaining
                            </span>
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-4 h-4" />
                              {pass.validity_days === 0 || (new Date(pass.expiry_date) - new Date()) / (1000 * 60 * 60 * 24 * 365) > 5
                                ? 'Never Expires'
                                : `Expires ${format(new Date(pass.expiry_date), 'MMM d, yyyy')}`
                              }
                            </span>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => openAdjustDialog(pass)}>
                            <History className="w-4 h-4 mr-1" />Adjust
                          </Button>
                        </div>
                      </div>
                    ))}
                    {clientDetails.class_passes.filter(pass => {
                      const matchesSearch = !classPassSearch || 
                        pass.class_pass_name?.toLowerCase().includes(classPassSearch.toLowerCase()) ||
                        pass.activity_type?.toLowerCase().includes(classPassSearch.toLowerCase());
                      const matchesStatus = classPassStatusFilter === 'all' || pass.status === classPassStatusFilter;
                      return matchesSearch && matchesStatus;
                    }).length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No class passes match your filters</p>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No ClassPasses purchased</p>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Client Profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="client@example.com"
                data-testid="client-edit-email"
              />
              <p className="text-xs text-muted-foreground">
                The client uses this email to log in. Changing it will update their sign-in credentials.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="+353 1234567" />
            </div>
            <div className="space-y-2">
              <Label>Address Line 1</Label>
              <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} placeholder="Street address" />
            </div>
            <div className="space-y-2">
              <Label>Address Line 2</Label>
              <Input value={editForm.address_line_2} onChange={(e) => setEditForm({ ...editForm, address_line_2: e.target.value })} placeholder="Apartment, suite, etc. (optional)" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City/Town</Label>
                <Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>County</Label>
                <Input value={editForm.county} onChange={(e) => setEditForm({ ...editForm, county: e.target.value })} placeholder="e.g., Dublin" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Eircode/Postal Code</Label>
                <Input value={editForm.postal_code} onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })} placeholder="e.g., D02 XY45" />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={editLoading}>
                {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust ClassPass Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust ClassPass Credits</DialogTitle>
            <DialogDescription>
              {selectedPass?.class_pass_name} - Currently {selectedPass?.passes_remaining} credits remaining
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdjustClassPass} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Adjustment Amount</Label>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="icon" onClick={() => setAdjustAmount(adjustAmount - 1)}>
                  <Minus className="w-4 h-4" />
                </Button>
                <Input 
                  type="number"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(parseInt(e.target.value) || 0)}
                  className="w-24 text-center"
                />
                <Button type="button" variant="outline" size="icon" onClick={() => setAdjustAmount(adjustAmount + 1)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Positive to add credits, negative to remove. New balance: {(selectedPass?.passes_remaining || 0) + adjustAmount}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Input 
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="e.g., Goodwill credit, correction, etc."
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdjustDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={adjustLoading || adjustAmount === 0 || !adjustReason}>
                {adjustLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply Adjustment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust Credits Dialog */}
      <Dialog open={showAdjustCreditsDialog} onOpenChange={setShowAdjustCreditsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Adjust Credits on Account
            </DialogTitle>
            <DialogDescription>
              Add or deduct credits from {selectedClient?.first_name}'s account
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdjustCredits} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Number of Credits *</Label>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="icon" onClick={() => setAdjustCreditsAmount(adjustCreditsAmount - 1)}>
                  <Minus className="w-4 h-4" />
                </Button>
                <Input 
                  type="number"
                  value={adjustCreditsAmount}
                  onChange={(e) => setAdjustCreditsAmount(parseInt(e.target.value) || 0)}
                  className="w-24 text-center"
                />
                <Button type="button" variant="outline" size="icon" onClick={() => setAdjustCreditsAmount(adjustCreditsAmount + 1)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Use positive numbers to add credits, negative to deduct
              </p>
            </div>
            <div className="space-y-2">
              <Label>Activity Type *</Label>
              <Select value={adjustCreditsActivityType} onValueChange={setAdjustCreditsActivityType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select activity type" />
                </SelectTrigger>
                <SelectContent>
                  {activityTypes.map(type => (
                    <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                  ))}
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Adjustment will be applied to classes of this activity type
              </p>
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Input 
                value={adjustCreditsReason}
                onChange={(e) => setAdjustCreditsReason(e.target.value)}
                placeholder="e.g., Compensation, correction, loyalty reward"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdjustCreditsDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={adjustCreditsLoading || adjustCreditsAmount === 0 || !adjustCreditsReason}>
                {adjustCreditsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    {adjustCreditsAmount > 0 ? <Plus className="w-4 h-4 mr-1" /> : <Minus className="w-4 h-4 mr-1" />}
                    {adjustCreditsAmount > 0 ? 'Add' : 'Deduct'} {Math.abs(adjustCreditsAmount)} Credit{Math.abs(adjustCreditsAmount) !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credit History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Credit History
            </DialogTitle>
            <DialogDescription>
              All credit transactions for {selectedClient?.first_name} {selectedClient?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-2">
              {creditHistory.length > 0 ? (
                creditHistory.map((entry, i) => (
                  <div key={entry.id || i} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        entry.amount > 0 ? 'bg-emerald-100' : 'bg-red-100'
                      }`}>
                        {entry.amount > 0 ? (
                          <ArrowUpCircle className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <ArrowDownCircle className="w-4 h-4 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{entry.description}</p>
                        <p className="text-xs text-muted-foreground">{entry.reason}</p>
                        {entry.class_name && (
                          <p className="text-xs text-muted-foreground">Class: {entry.class_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${entry.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {entry.amount > 0 ? '+' : ''}{entry.amount}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {entry.created_at ? format(new Date(entry.created_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">No credit history found</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={showCancelBookingDialog} onOpenChange={setShowCancelBookingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
            <DialogDescription>
              Cancel this booking for {selectedClient?.first_name} {selectedClient?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="p-4 border rounded-lg bg-muted/50">
                <p className="font-medium">{selectedBooking.class_name}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedBooking.class_start_time 
                    ? format(new Date(selectedBooking.class_start_time), 'EEEE, MMM d, yyyy • h:mm a') 
                    : 'Date not available'}
                </p>
                {selectedBooking.used_class_pass && (
                  <Badge variant="secondary" className="mt-2">Booked with ClassPass</Badge>
                )}
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="font-medium">Refund credit to client</Label>
                  <p className="text-sm text-muted-foreground">
                    {selectedBooking.used_class_pass 
                      ? 'Return the ClassPass credit used for this booking'
                      : 'Issue a refund for this booking'}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={cancelWithRefund}
                  onChange={(e) => setCancelWithRefund(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300"
                />
              </div>

              <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> The client will receive a notification about this cancellation.
                  {cancelWithRefund && selectedBooking.used_class_pass && ' Their ClassPass credit will be refunded.'}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelBookingDialog(false)}>
              Keep Booking
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelBooking}
              disabled={cancelLoading}
              data-testid="confirm-cancel-booking-btn"
            >
              {cancelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Cancel Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Clients Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        setShowImportDialog(open);
        if (!open) {
          setImportFile(null);
          setImportResults(null);
          setImportSendWelcome(false);
          setImportAssignPass('');
          setImportCredits(0);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Import Clients
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with client information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Download Template */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-sm">Download Template</p>
                <p className="text-xs text-muted-foreground">Get the CSV format template</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <FileText className="w-4 h-4 mr-2" />
                Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="import-file-input"
              />
              {importFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {importFile.name}
                </p>
              )}
            </div>

            {/* Options */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send-welcome"
                  checked={importSendWelcome}
                  onCheckedChange={setImportSendWelcome}
                />
                <Label htmlFor="send-welcome" className="text-sm font-normal">
                  Send welcome email to new clients
                </Label>
              </div>

              <div className="space-y-2">
                <Label>Assign ClassPass (optional)</Label>
                <Select value={importAssignPass} onValueChange={setImportAssignPass}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select ClassPass..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {classPasses.map(pass => (
                      <SelectItem key={pass.id} value={pass.id}>
                        {pass.name} ({pass.passes_count} classes)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {importAssignPass && importAssignPass !== 'none' && (
                <div className="space-y-2">
                  <Label>Credits per client</Label>
                  <Input
                    type="number"
                    min="1"
                    value={importCredits}
                    onChange={(e) => setImportCredits(parseInt(e.target.value) || 0)}
                  />
                </div>
              )}
            </div>

            {/* Results */}
            {importResults && (
              <div className="p-4 rounded-lg border bg-muted/50">
                <h4 className="font-medium mb-2">Import Results</h4>
                <div className="space-y-1 text-sm">
                  <p className="text-emerald-600">✓ Imported: {importResults.imported}</p>
                  <p className="text-amber-600">⊘ Skipped: {importResults.skipped}</p>
                  {importResults.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-destructive font-medium">Errors:</p>
                      <ul className="list-disc list-inside text-xs text-destructive">
                        {importResults.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {importResults.errors.length > 5 && (
                          <li>...and {importResults.errors.length - 5} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleImportClients} disabled={!importFile || importing}>
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
